import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, LogIn, Loader2, ShieldCheck } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { adminLogin, isAdminLoggedIn } from "../services/api";
import { useToast } from "../hooks/use-toast";

const AdminLoginPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [form, setForm] = useState({ username: "", password: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isAdminLoggedIn()) navigate("/admin/dashboard");
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await adminLogin(form.username, form.password);
      toast({ title: "Welcome back!", description: "You are now logged in." });
      navigate("/admin/dashboard");
    } catch (err) {
      toast({
        title: "Login failed",
        description: err.response?.data?.detail || "Invalid credentials",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center py-16 bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50">
      <div className="w-full max-w-md mx-4">
        <div className="bg-white rounded-3xl shadow-2xl border border-orange-100 p-8 md:p-10">
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-orange-600 to-red-600 flex items-center justify-center shadow-lg">
              <ShieldCheck className="w-8 h-8 text-white" />
            </div>
            <h1 className="font-display text-3xl font-bold text-stone-800">Admin Portal</h1>
            <p className="text-stone-500 text-sm mt-1">Aadi Gayanam Play School</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="username" className="text-stone-700 font-semibold">Username</Label>
              <Input
                id="username"
                required
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
                placeholder="admin"
                className="mt-1.5 h-11 bg-orange-50/50 border-orange-200 focus-visible:ring-orange-500"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-stone-700 font-semibold">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-orange-400" />
                <Input
                  id="password"
                  type="password"
                  required
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="••••••••"
                  className="mt-1.5 h-11 pl-9 bg-orange-50/50 border-orange-200 focus-visible:ring-orange-500"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-gradient-to-r from-orange-600 to-red-600 text-white rounded-full shadow-lg btn-shine mt-2"
            >
              {loading ? (
                <><Loader2 className="mr-2 w-4 h-4 animate-spin" /> Signing in...</>
              ) : (
                <><LogIn className="mr-2 w-4 h-4" /> Sign In</>
              )}
            </Button>
          </form>

          <p className="text-center text-xs text-stone-400 mt-6">
            Authorized personnel only. Contact IT for access.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminLoginPage;
